import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import { useToast } from '@radiology/ui';
import dayjs from 'dayjs';
import { money, useDecideRequest, type PurchaseRequest, type RequestStatus } from './api';

/**
 * The one decision dialog, for every stage of the chain.
 *
 * `approveAs` says which forward step this reviewer is taking — `Approved` for an
 * approver, `Chief approved` for a chief approver. Rejection is the same action
 * for both.
 *
 * The rules live in the database: a rejection must carry a comment, the chain
 * cannot be skipped, and the timestamps are stamped by the trigger. This dialog
 * mirrors the comment requirement so the user finds out before submitting, but
 * the trigger is what enforces it — if the two ever disagree, the database wins
 * and its message is shown.
 */
export function DecisionDialog({
  request,
  approveAs,
  reviewerName,
  reviewerRole,
  onClose,
}: {
  request: PurchaseRequest | null;
  approveAs: Extract<RequestStatus, 'Approved' | 'Chief approved'>;
  reviewerName: string;
  reviewerRole: string;
  onClose: () => void;
}) {
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const decide = useDecideRequest();
  const toast = useToast();

  const submit = (status: RequestStatus) => {
    if (!request) return;
    if (status === 'Rejected' && !comment.trim()) {
      setError('A rejection needs a reason — it is the only thing the requester is told.');
      return;
    }
    setError(null);
    decide.mutate(
      {
        id: request.request_id,
        status,
        comment: comment.trim(),
        existing: request.comments,
        reviewerName,
        reviewerRole,
      },
      {
        onSuccess: () => {
          toast.success(status === 'Rejected' ? 'Request rejected' : `Request marked ${status}`);
          setComment('');
          onClose();
        },
        onError: (e: Error) => setError(e.message),
      },
    );
  };

  const history = request?.comments ?? [];
  const approveLabel = approveAs === 'Chief approved' ? 'Chief approve' : 'Approve';

  return (
    <Dialog open={Boolean(request)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle variant="h4">Review purchase request</DialogTitle>
      <DialogContent>
        {request && (
          <Stack gap={2}>
            <Stack gap={0.5}>
              <Typography variant="h4">{request.description}</Typography>
              <Typography variant="body2" color="text.secondary">
                {request.creator_username} · {request.creator_hospital} · {request.creator_section}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Raised {dayjs(request.created_at).format('D MMM YYYY')}
              </Typography>
            </Stack>

            <Divider />

            <Grid container spacing={1.5}>
              {[
                { label: 'Serial number', value: request.serial_no },
                { label: 'Catalogue number', value: request.cat_no ?? '—' },
                {
                  label: 'Quantity',
                  value: `${request.quantity} ${request.unit_of_order ?? ''}`.trim(),
                },
                { label: 'Unit price', value: money(request.unit_price) },
                { label: 'Total', value: money(request.total) },
                { label: 'Existing item', value: request.existing_item },
                { label: 'Section head', value: request.section_head_signature },
                { label: 'Department', value: request.department ?? '—' },
              ].map(field => (
                <Grid key={field.label} size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    {field.label}
                  </Typography>
                  <Typography variant="body2">{field.value}</Typography>
                </Grid>
              ))}
            </Grid>

            <Stack gap={0.25}>
              <Typography variant="caption" color="text.secondary">
                Justification
              </Typography>
              <Typography variant="body2">{request.justification}</Typography>
            </Stack>

            {history.length > 0 && (
              <>
                <Divider />
                <Stack gap={1}>
                  <Typography variant="overline" color="text.secondary">
                    Previous comments
                  </Typography>
                  {history.map((c, i) => (
                    <Stack key={i} gap={0.25}>
                      <Typography variant="body2">{c.comment}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.authorName ?? 'Unknown'}
                        {c.authorRole ? ` (${c.authorRole})` : ''} · {c.createdAt ?? ''}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Comment"
              helperText="Required when rejecting. The requester sees this."
              multiline
              minRows={2}
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit" disabled={decide.isPending}>
          Cancel
        </Button>
        <Button
          onClick={() => submit('Rejected')}
          color="error"
          variant="outlined"
          disabled={decide.isPending}
        >
          Reject
        </Button>
        <Button onClick={() => submit(approveAs)} variant="contained" disabled={decide.isPending}>
          {decide.isPending ? 'Saving…' : approveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
